import React, { useState, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { ProjectCard } from './ProjectCard';
import { ProjectBoardDto, BoardColumnDto, ProjectCardDto } from '../../services/projects';
import './ProjectKanban.css';

interface ProjectKanbanProps {
  board: ProjectBoardDto;
  onProjectClick?: (project: ProjectCardDto) => void;
  onArchiveProject?: (project: ProjectCardDto) => void;
  onMoveProject?: (projectId: string, newStage: string) => void;
  onCreateProject?: (stage: string) => void;
}

export const ProjectKanban: React.FC<ProjectKanbanProps> = ({
  board,
  onProjectClick,
  onArchiveProject,
  onMoveProject,
  onCreateProject,
}) => {
  const [draggedProject, setDraggedProject] = useState<ProjectCardDto | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, project: ProjectCardDto) => {
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', project.id);

    // Add a slight delay before adding the dragging class
    const target = e.target as HTMLElement;
    setTimeout(() => target.classList.add('is-dragging'), 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const target = e.target as HTMLElement;
    target.classList.remove('is-dragging');
    setDraggedProject(null);
    setDragOverColumn(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, column: BoardColumnDto) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(column.stageName);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear if we're leaving the column, not entering a child
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;
    if (!currentTarget.contains(relatedTarget)) {
      setDragOverColumn(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, column: BoardColumnDto) => {
    e.preventDefault();
    const projectId = e.dataTransfer.getData('text/plain');

    if (draggedProject && draggedProject.currentStage !== column.stageName) {
      onMoveProject?.(projectId, column.stageName);
    }

    setDraggedProject(null);
    setDragOverColumn(null);
  }, [draggedProject, onMoveProject]);

  const isAtLimit = board.activeProjects >= board.projectLimit;
  const columnCount = board.columns.length;

  // Determine layout mode based on column count
  // - 4 or fewer: single-row grid
  // - 5-6: compact two-row layout
  // - 7+: vertical timeline/swimlane
  const layoutMode = columnCount <= 4 ? 'grid' : columnCount <= 6 ? 'compact' : 'timeline';

  return (
    <div className={`project-kanban project-kanban--${layoutMode} project-kanban--cols-${columnCount}`}>
      {/* Board Header Stats */}
      <div className="project-kanban__stats">
        <div className="project-kanban__stat">
          <span className="project-kanban__stat-value">{board.activeProjects}</span>
          <span className="project-kanban__stat-label">Active</span>
        </div>
        <div className="project-kanban__stat">
          <span className="project-kanban__stat-value">{board.archivedProjects}</span>
          <span className="project-kanban__stat-label">Archived</span>
        </div>
        <div className={`project-kanban__limit ${isAtLimit ? 'project-kanban__limit--reached' : ''}`}>
          {isAtLimit && <AlertCircle size={14} />}
          <span>{board.activeProjects}/{board.projectLimit} Projects</span>
        </div>
      </div>

      {/* Board Columns */}
      <div className="project-kanban__board">
        {board.columns.map((column) => (
          <div
            key={column.stageName}
            className={`project-kanban__column ${dragOverColumn === column.stageName ? 'project-kanban__column--drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, column)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column)}
          >
            <div className="project-kanban__column-header">
              <h3 className="project-kanban__column-title">{column.stageName}</h3>
              <span className="project-kanban__column-count">{column.projects.length}</span>
            </div>

            <div className="project-kanban__column-content">
              {column.projects.map((project) => (
                <div
                  key={project.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, project)}
                  onDragEnd={handleDragEnd}
                  className="project-kanban__card-wrapper"
                >
                  <ProjectCard
                    project={project}
                    onView={onProjectClick}
                    onArchive={onArchiveProject}
                    isDragging={draggedProject?.id === project.id}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectKanban;
